import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LayoutDashboard,
  FileText,
  Scale,
  Bell,
  LogOut,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  AlertCircle,
  PenTool,
  BarChart3,
  Mail,
  Paperclip,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Check if company should have Letters (IFG or Awtad)
const hasLettersModule = (companyName) => {
  if (!companyName) return false;
  return companyName.includes('IFG') || companyName.includes('القابضة') || companyName.includes('أوتاد');
};

export default function ExecutiveDashboard({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'contracts';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [contracts, setContracts] = useState([]);
  const [cases, setCases] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [letters, setLetters] = useState([]);
  const [annexes, setAnnexes] = useState({});
  const [expandedAgreements, setExpandedAgreements] = useState({});
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [letterTypeFilter, setLetterTypeFilter] = useState('all');
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedCompany, statusFilter, searchTerm, activeTab, letterTypeFilter]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('فشل تحميل الشركات');
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/stats`, {
          params: { company_id: selectedCompany !== 'all' ? selectedCompany : undefined },
        }),
        axios.get(`${API}/notifications`),
      ]);

      setStats(statsRes.data);
      setNotifications(notificationsRes.data);

      if (activeTab === 'contracts') {
        const contractsRes = await axios.get(`${API}/contracts`, {
          params: {
            company_id: selectedCompany !== 'all' ? selectedCompany : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setContracts(contractsRes.data);
      } else if (activeTab === 'cases') {
        const casesRes = await axios.get(`${API}/cases`, {
          params: {
            company_id: selectedCompany !== 'all' ? selectedCompany : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setCases(casesRes.data);
      } else if (activeTab === 'agreements') {
        const agreementsRes = await axios.get(`${API}/agreements`, {
          params: {
            company_id: selectedCompany !== 'all' ? selectedCompany : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setAgreements(agreementsRes.data);
      } else if (activeTab === 'commitments') {
        const commitmentsRes = await axios.get(`${API}/commitments`, {
          params: {
            company_id: selectedCompany !== 'all' ? selectedCompany : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setCommitments(commitmentsRes.data);
      } else if (activeTab === 'letters') {
        const lettersRes = await axios.get(`${API}/letters`, {
          params: {
            company_id: selectedCompany !== 'all' ? selectedCompany : undefined,
            letter_type: letterTypeFilter !== 'all' ? letterTypeFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setLetters(lettersRes.data);
      }
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  // Fetch annexes for an agreement
  const fetchAnnexes = async (agreementId) => {
    try {
      const response = await axios.get(`${API}/agreements/${agreementId}/annexes`);
      setAnnexes(prev => ({ ...prev, [agreementId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch annexes:', error);
    }
  };

  // Toggle agreement expansion
  const toggleAgreementExpand = (agreementId) => {
    setExpandedAgreements(prev => {
      const newState = { ...prev, [agreementId]: !prev[agreementId] };
      if (newState[agreementId] && !annexes[agreementId]) {
        fetchAnnexes(agreementId);
      }
      return newState;
    });
  };

  const handleApprove = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/approve`);
      toast.success('تم اعتماد العقد بنجاح');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل اعتماد العقد');
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`);
      fetchData();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('تم تسجيل الخروج بنجاح');
  };

  const getContractStatusBadge = (status) => {
    const statusMap = {
      draft: { text: 'مسودة', class: 'status-draft' },
      review: { text: 'قيد المراجعة', class: 'status-review' },
      approved: { text: 'معتمد', class: 'status-approved' },
      signed: { text: 'موقّع', class: 'status-signed' },
    };
    const statusInfo = statusMap[status] || statusMap.draft;
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const getCaseStatusBadge = (status) => {
    const statusMap = {
      new: { text: 'جديدة', class: 'status-new' },
      filed: { text: 'مرفوعة', class: 'status-filed' },
      hearings: { text: 'جلسات', class: 'status-hearings' },
      final_judgment: { text: 'حكم نهائي', class: 'status-final_judgment' },
      execution: { text: 'تنفيذ', class: 'status-execution' },
    };
    const statusInfo = statusMap[status] || statusMap.new;
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-64 bg-gradient-to-b from-slate-800 to-slate-900 text-slate-300 z-50">
        <div className="p-6">
          <img
            src="https://customer-assets.emergentagent.com/job_contract-portal-1/artifacts/qhuwq0xk_IFG%20logo.png"
            alt="IFG Logo"
            className="h-16 mb-8 mx-auto"
          />
          <nav className="space-y-2">
            <button
              className="sidebar-item active w-full text-right px-4 py-3 rounded-md flex items-center gap-3"
              data-testid="dashboard-nav-item"
            >
              <LayoutDashboard size={20} />
              <span>لوحة التحكم</span>
            </button>
            <button
              className="sidebar-item w-full text-right px-4 py-3 rounded-md flex items-center gap-3"
              onClick={() => navigate('/search')}
              data-testid="search-nav-item"
            >
              <Search size={20} />
              <span>بحث</span>
            </button>
            <button
              className="sidebar-item w-full text-right px-4 py-3 rounded-md flex items-center gap-3"
              onClick={() => navigate('/profile')}
              data-testid="profile-nav-item"
            >
              <PenTool size={20} />
              <span>الملف الشخصي</span>
            </button>
            <button
              className="sidebar-item w-full text-right px-4 py-3 rounded-md flex items-center gap-3 relative"
              onClick={() => setIsNotificationsOpen(true)}
              data-testid="notifications-nav-item"
            >
              <Bell size={20} />
              <span>الإشعارات</span>
              {unreadCount > 0 && (
                <span className="absolute left-4 top-3 bg-[#E94B7B] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          </nav>
        </div>
        <div className="absolute bottom-0 w-full p-6 border-t border-slate-700">
          <div className="mb-4 text-sm">
            <p className="font-medium text-white">{user?.full_name}</p>
            <p className="text-xs text-slate-400">الرئيس التنفيذي</p>
            {user?.last_login && (
              <p className="text-slate-500 text-xs mt-1">
                آخر دخول: {new Date(user.last_login).toLocaleDateString('ar-SA')} - {new Date(user.last_login).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700"
            data-testid="logout-button"
          >
            <LogOut size={18} className="ml-2" />
            تسجيل الخروج
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="mr-64 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            مرحباً، {user?.full_name}
          </h1>
          <p className="text-slate-600">مراجعة العقود والقضايا لمجموعة IFG</p>
        </div>

        {/* Company Selector */}
        <div className="mb-6">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-64 h-11 bg-white border-slate-200" data-testid="company-select">
              <SelectValue placeholder="اختر الشركة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الشركات</SelectItem>
              {companies.map((company) => (
                <SelectItem key={company.id} value={company.id}>
                  {company.name_ar}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <FileText className="text-slate-600" size={24} />
              <span className="text-3xl font-bold text-slate-900">{stats.contracts?.total || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">إجمالي العقود</p>
          </div>
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-amber-600" size={24} />
              <span className="text-3xl font-bold text-amber-600">{stats.contracts?.review || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">قيد المراجعة</p>
          </div>
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <PenTool className="text-indigo-600" size={24} />
              <span className="text-3xl font-bold text-indigo-600">{stats.contracts?.signed || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">موقّعة</p>
          </div>
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <Scale className="text-[#E94B7B]" size={24} />
              <span className="text-3xl font-bold text-[#E94B7B]">{stats.cases?.total || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">إجمالي القضايا</p>
          </div>
          <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="text-blue-600" size={24} />
              <span className="text-3xl font-bold text-blue-600">{stats.cases?.hearings || 0}</span>
            </div>
            <p className="text-sm text-slate-600 font-medium">جلسات</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 mb-6">
          <div className="flex gap-8">
            <button
              onClick={() => {
                setActiveTab('contracts');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`tab-button ${activeTab === 'contracts' ? 'active' : ''}`}
              data-testid="contracts-tab"
            >
              <FileText size={18} className="inline-block ml-2" />
              العقود
            </button>
            <button
              onClick={() => {
                setActiveTab('cases');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`tab-button ${activeTab === 'cases' ? 'active' : ''}`}
              data-testid="cases-tab"
            >
              <Scale size={18} className="inline-block ml-2" />
              القضايا
            </button>
            <button
              onClick={() => {
                setActiveTab('agreements');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`tab-button ${activeTab === 'agreements' ? 'active' : ''}`}
              data-testid="agreements-tab"
            >
              <FileText size={18} className="inline-block ml-2" />
              الاتفاقيات
            </button>
            <button
              onClick={() => {
                setActiveTab('commitments');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`tab-button ${activeTab === 'commitments' ? 'active' : ''}`}
              data-testid="commitments-tab"
            >
              <Scale size={18} className="inline-block ml-2" />
              الإقرارات
            </button>
            {/* تبويب الخطابات - IFG و أوتاد العمران */}
            {(selectedCompany === 'all' || hasLettersModule(companies.find(c => c.id === selectedCompany)?.name_ar)) && (
              <button
                onClick={() => {
                  setActiveTab('letters');
                  setLetterTypeFilter('all');
                  setSearchTerm('');
                }}
                className={`tab-button ${activeTab === 'letters' ? 'active' : ''}`}
                data-testid="letters-tab"
              >
                <Mail size={18} className="inline-block ml-2" />
                الخطابات
              </button>
            )}
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              placeholder={`البحث في ${activeTab === 'contracts' ? 'العقود' : activeTab === 'cases' ? 'القضايا' : activeTab === 'agreements' ? 'الاتفاقيات' : activeTab === 'letters' ? 'الخطابات' : 'الإقرارات'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10 h-11 bg-white border-slate-200"
              data-testid="search-input"
            />
          </div>
          {activeTab === 'letters' ? (
            <Select value={letterTypeFilter} onValueChange={setLetterTypeFilter}>
              <SelectTrigger className="w-full sm:w-48 h-11 bg-white border-slate-200" data-testid="filter-type-select">
                <Filter size={16} className="ml-2" />
                <SelectValue placeholder="تصفية حسب النوع" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الخطابات</SelectItem>
                <SelectItem value="outgoing">صادر</SelectItem>
                <SelectItem value="incoming">وارد</SelectItem>
              </SelectContent>
            </Select>
          ) : (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 h-11 bg-white border-slate-200" data-testid="filter-status-select">
              <Filter size={16} className="ml-2" />
              <SelectValue placeholder="تصفية حسب الحالة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الحالات</SelectItem>
              {activeTab === 'contracts' ? (
                <>
                  <SelectItem value="draft">مسودة</SelectItem>
                  <SelectItem value="review">قيد المراجعة</SelectItem>
                  <SelectItem value="approved">معتمد</SelectItem>
                  <SelectItem value="signed">موقّع</SelectItem>
                </>
              ) : activeTab === 'cases' ? (
                <>
                  <SelectItem value="new">جديدة</SelectItem>
                  <SelectItem value="filed">مرفوعة</SelectItem>
                  <SelectItem value="hearings">جلسات</SelectItem>
                  <SelectItem value="final_judgment">حكم نهائي</SelectItem>
                  <SelectItem value="execution">تنفيذ</SelectItem>
                </>
              ) : activeTab === 'agreements' ? (
                <>
                  <SelectItem value="draft">مسودة</SelectItem>
                  <SelectItem value="review">قيد المراجعة</SelectItem>
                  <SelectItem value="approved">معتمدة</SelectItem>
                  <SelectItem value="signed">مُفعّلة</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="draft">مسودة</SelectItem>
                  <SelectItem value="review">قيد المراجعة</SelectItem>
                  <SelectItem value="approved">معتمد</SelectItem>
                  <SelectItem value="signed">موقّع</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          )}
        </div>

        {/* Data List */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : activeTab === 'contracts' ? (
            contracts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <FileText size={48} className="mb-4" />
                <p className="text-lg">لا توجد عقود</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">عنوان العقد</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">نوع العقد</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الطرف الثاني</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">تاريخ الإنشاء</th>
                      {selectedCompany === 'all' && (
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الشركة</th>
                      )}
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {contracts.map((contract) => (
                      <tr
                        key={contract.id}
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/contract/${contract.id}`)}
                        data-testid={`contract-row-${contract.id}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{contract.title}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{contract.contract_type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{contract.party_name}</td>
                        <td className="px-6 py-4">{getContractStatusBadge(contract.status)}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {new Date(contract.created_at).toLocaleDateString('ar-SA')}
                        </td>
                        {selectedCompany === 'all' && (
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {companies.find(c => c.id === contract.company_id)?.name_ar || '-'}
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <span className="text-xs text-blue-600 hover:text-blue-800 font-medium">استعراض</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : activeTab === 'cases' ? (
            cases.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Scale size={48} className="mb-4" />
                <p className="text-lg">لا توجد قضايا</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">رقم القضية</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">عنوان القضية</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">نوع القضية</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">المدعي</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">تاريخ الإنشاء</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {cases.map((caseItem) => (
                      <tr
                        key={caseItem.id}
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/case/${caseItem.id}`)}
                        data-testid={`case-row-${caseItem.id}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{caseItem.case_number}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{caseItem.title}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{caseItem.case_type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{caseItem.plaintiff}</td>
                        <td className="px-6 py-4">{getCaseStatusBadge(caseItem.status)}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {new Date(caseItem.created_at).toLocaleDateString('ar-SA')}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs text-blue-600 hover:text-blue-800 font-medium">استعراض</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : activeTab === 'agreements' ? (
            agreements.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <FileText size={48} className="mb-4" />
                <p className="text-lg">لا توجد اتفاقيات</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-4 text-center text-xs font-bold text-slate-500 uppercase w-12">الملاحق</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">رقم الاتفاقية</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">العنوان</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الطرف الثاني</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">التاريخ</th>
                      {selectedCompany === 'all' && (
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الشركة</th>
                      )}
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {agreements.map((agreement) => (
                      <React.Fragment key={agreement.id}>
                        <tr
                          className="hover:bg-slate-50 transition-colors"
                          data-testid={`agreement-row-${agreement.id}`}
                        >
                          <td className="px-3 py-4 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="p-1 h-8 w-8"
                              onClick={() => toggleAgreementExpand(agreement.id)}
                              data-testid={`expand-annexes-${agreement.id}`}
                            >
                              {expandedAgreements[agreement.id] ? (
                                <ChevronUp size={18} className="text-slate-500" />
                              ) : (
                                <ChevronDown size={18} className="text-slate-500" />
                              )}
                            </Button>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{agreement.agreement_number}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{agreement.title}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{agreement.party_name}</td>
                          <td className="px-6 py-4">{getContractStatusBadge(agreement.status)}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {agreement.agreement_date ? new Date(agreement.agreement_date).toLocaleDateString('ar-SA') : '-'}
                          </td>
                          {selectedCompany === 'all' && (
                            <td className="px-6 py-4 text-sm text-slate-600">
                              {companies.find(c => c.id === agreement.company_id)?.name_ar || '-'}
                            </td>
                          )}
                          <td className="px-6 py-4">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={() => navigate(`/agreement/${agreement.id}`)}
                            >
                              استعراض
                            </Button>
                          </td>
                        </tr>
                        {/* صف الملاحق (يظهر عند التوسيع) */}
                        {expandedAgreements[agreement.id] && (
                          <tr className="bg-purple-50/50">
                            <td colSpan={selectedCompany === 'all' ? 8 : 7} className="px-6 py-4">
                              <div className="mr-8 border-r-2 border-purple-200 pr-4">
                                <h4 className="text-sm font-bold text-purple-700 mb-3 flex items-center gap-2">
                                  <Paperclip size={16} />
                                  ملاحق الاتفاقية
                                </h4>
                                {annexes[agreement.id]?.length > 0 ? (
                                  <div className="space-y-2">
                                    {annexes[agreement.id].map((annex) => (
                                      <div key={annex.id} className="bg-white rounded-lg border border-purple-200 p-3 flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-slate-900">{annex.annex_number}</span>
                                            <span className="text-sm text-slate-600">{annex.title}</span>
                                            {annex.annex_date && (
                                              <span className="text-xs text-slate-400">
                                                ({new Date(annex.annex_date).toLocaleDateString('ar-SA')})
                                              </span>
                                            )}
                                          </div>
                                          {annex.description && (
                                            <p className="text-xs text-slate-500 mt-1">{annex.description}</p>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {annex.file_name ? (
                                            <a
                                              href={`/api/files/annex/${annex.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                                            >
                                              <FileText size={14} />
                                              {annex.file_name.length > 12 ? annex.file_name.substring(0, 12) + '...' : annex.file_name}
                                            </a>
                                          ) : (
                                            <span className="text-xs text-slate-400">لا يوجد ملف</span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-500">لا توجد ملاحق لهذه الاتفاقية</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : activeTab === 'commitments' ? (
            commitments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Scale size={48} className="mb-4" />
                <p className="text-lg">لا توجد إقرارات</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">رقم الإقرار</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">اسم الموظف</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">نوع الإقرار</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">التاريخ</th>
                      {selectedCompany === 'all' && (
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الشركة</th>
                      )}
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {commitments.map((commitment) => (
                      <tr
                        key={commitment.id}
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`commitment-row-${commitment.id}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{commitment.commitment_number}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{commitment.employee_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{commitment.commitment_type}</td>
                        <td className="px-6 py-4">{getContractStatusBadge(commitment.status)}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {commitment.commitment_date ? new Date(commitment.commitment_date).toLocaleDateString('ar-SA') : '-'}
                        </td>
                        {selectedCompany === 'all' && (
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {companies.find(c => c.id === commitment.company_id)?.name_ar || '-'}
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            onClick={() => navigate(`/commitment/${commitment.id}`)}
                          >
                            استعراض
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : activeTab === 'letters' ? (
            letters.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Mail size={48} className="mb-4" />
                <p className="text-lg">لا توجد خطابات</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">رقم الخطاب</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الموضوع</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">النوع</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">المرسل</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">المرسل إليه</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">التاريخ</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الملف</th>
                      {selectedCompany === 'all' && (
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الشركة</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {letters.map((letter) => (
                      <tr
                        key={letter.id}
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`letter-row-${letter.id}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{letter.letter_number}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{letter.subject}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            letter.letter_type === 'outgoing' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {letter.letter_type === 'outgoing' ? 'صادر' : 'وارد'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{letter.sender || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{letter.recipient || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {letter.letter_date ? new Date(letter.letter_date).toLocaleDateString('ar-SA') : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {letter.file_name ? (
                            <a
                              href={`/api/files/letter/${letter.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <FileText size={14} />
                              {letter.file_name.length > 15 ? letter.file_name.substring(0, 15) + '...' : letter.file_name}
                            </a>
                          ) : (
                            <span className="text-slate-400">لا يوجد ملف</span>
                          )}
                        </td>
                        {selectedCompany === 'all' && (
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {companies.find(c => c.id === letter.company_id)?.name_ar || '-'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
        </div>
      </div>

      {/* Notifications Dialog */}
      <Dialog open={isNotificationsOpen} onOpenChange={setIsNotificationsOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-right">الإشعارات</DialogTitle>
            <DialogDescription className="text-right">
              آخر التحديثات والإشعارات
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3 mt-4">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell size={48} className="mx-auto mb-4 opacity-50" />
                <p>لا توجد إشعارات</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    notification.read
                      ? 'bg-slate-50 border-slate-200'
                      : 'bg-pink-50 border-pink-200'
                  }`}
                  onClick={() => {
                    markNotificationAsRead(notification.id);
                    if (notification.related_type === 'contract' && notification.related_id) {
                      navigate(`/contract/${notification.related_id}`);
                      setIsNotificationsOpen(false);
                    } else if (notification.related_type === 'case' && notification.related_id) {
                      navigate(`/case/${notification.related_id}`);
                      setIsNotificationsOpen(false);
                    }
                  }}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900 mb-1">{notification.title}</h4>
                      <p className="text-sm text-slate-600">{notification.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(notification.created_at).toLocaleString('ar-SA')}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-[#E94B7B] rounded-full mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
